import { useCallback, useMemo, useState } from "react"
import { Button, Badge, Collapse } from "reactstrap"

const ButtonCheckboxList = ({
  items = [],
  selected = [],
  onChange = () => {},
  maxVisibleItems = 30,
  showSelectAll = true,
  showSelectionSummary = true,
  isShowHeader = true,
}) => {
  const [expanded, setExpanded] = useState(false)
  const itemIds = useMemo(() => items.map(item => item.id), [items])
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const toggleItem = useCallback(
    id => {
      const newSelected = new Set(selectedSet)
      newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id)
      onChange(Array.from(newSelected))
    },
    [selectedSet, onChange]
  )

  const toggleAll = useCallback(() => {
    selectedSet.size === items.length ? onChange([]) : onChange(itemIds)
  }, [selectedSet.size, items.length, itemIds, onChange])

  const toggleExpand = useCallback(() => {
    setExpanded(prev => !prev)
  }, [])

  const allSelected = useMemo(
    () => selectedSet.size === items.length && items.length > 0,
    [selectedSet.size, items.length]
  )

  const someSelected = useMemo(
    () => selectedSet.size > 0 && selectedSet.size < items.length,
    [selectedSet.size, items.length]
  )

  // Calculate visible and overflow items
  const { visibleItems, overflowItems, hasOverflow } = useMemo(() => {
    const visibleCount = expanded ? items.length : maxVisibleItems
    return {
      visibleItems: items.slice(0, visibleCount),
      overflowItems: items.slice(visibleCount),
      hasOverflow: items.length > maxVisibleItems,
    }
  }, [items, maxVisibleItems, expanded])

  // Memoize the header section with selection info
  const headerSection = useMemo(
    () => (
      <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded-3 border">
        <div className="d-flex align-items-center gap-3">
          {showSelectAll && (
            <Button
              size="sm"
              color={
                allSelected
                  ? "success"
                  : someSelected
                  ? "warning"
                  : "outline-secondary"
              }
              className="d-flex align-items-center gap-2 "
              onClick={toggleAll}
            >
              <i
                className={`bx ${
                  allSelected
                    ? "bx-checkbox-checked"
                    : someSelected
                    ? "bx-checkbox-minus"
                    : "bx-checkbox"
                }`}
              ></i>
              <span className="fw-medium">
                {allSelected ? "Deselect All" : "Select All"}
              </span>
            </Button>
          )}

          <Badge
            color={selectedSet.size > 0 ? "success" : "secondary"}
            className="fs-6 "
          >
            {selectedSet.size} of {items.length} selected
          </Badge>
        </div>

        <div className="d-flex align-items-center gap-2">
          {selectedSet.size > 0 && (
            <Button
              size="sm"
              color="outline-danger"
              className="d-flex align-items-center gap-2"
              onClick={() => onChange([])}
            >
              <i className="bx bx-x"></i>
              Clear
            </Button>
          )}

          {hasOverflow && (
            <Button
              size="sm"
              color="outline-primary"
              className="d-flex align-items-center gap-2"
              onClick={toggleExpand}
            >
              <i
                className={`bx ${
                  expanded ? "bx-chevron-up" : "bx-chevron-down"
                }`}
              ></i>
              {expanded ? "Show Less" : `Show All (${items.length})`}
            </Button>
          )}
        </div>
      </div>
    ),
    [
      allSelected,
      someSelected,
      selectedSet.size,
      items.length,
      hasOverflow,
      expanded,
      showSelectAll,
      toggleAll,
      onChange,
      toggleExpand,
    ]
  )

  // Memoize individual item buttons
  const renderItemButton = item => {
    const isSelected = selectedSet.has(item.id)

    return (
      <Button
        key={item.id}
        size="sm"
        color={isSelected ? "success" : "outline-secondary"}
        className="d-flex align-items-center gap-2  item-button"
        style={{
          minWidth: "120px",
          transition: "all 0.2s ease-in-out",
        }}
        onClick={() => toggleItem(item.id)}
      >
        <i
          className={`bx ${
            isSelected ? "bx-checkbox-checked" : "bx-checkbox"
          } fs-6`}
        ></i>
        <span className="text-truncate" style={{ maxWidth: "150px" }}>
          {item.name}
        </span>
        {item.badge && (
          <Badge color="light" text="dark" className="ms-1 fs-7">
            {item.badge}
          </Badge>
        )}
      </Button>
    )
  }

  // Main items container with smooth expansion
  const itemsContainer = useMemo(
    () => (
      <div className="items-container">
        <div className="d-flex flex-wrap gap-2 mb-2">
          {visibleItems.map(renderItemButton)}
        </div>

        {/* Expandable section with smooth animation */}
        {hasOverflow && (
          <Collapse isOpen={expanded}>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {overflowItems.map(renderItemButton)}
            </div>
          </Collapse>
        )}

        {/* Overflow summary when collapsed */}
        {hasOverflow && !expanded && overflowItems.length > 0 && (
          <div className="mt-2">
            <div className="position-relative d-inline-block">
              <Button
                size="sm"
                color="outline-info"
                className="d-flex align-items-center gap-2 px-3"
                onClick={toggleExpand}
              >
                <i className="bx bx-chevron-down fs-6"></i>
                <span>+{overflowItems.length} more items</span>
              </Button>
            </div>

            {/* Quick selection for overflow items */}
            <div className="mt-2 d-flex gap-1 flex-wrap">
              <Button
                size="sm"
                color="outline-success"
                className="d-flex align-items-center gap-1"
                onClick={() => {
                  const newSelected = new Set(selectedSet)
                  overflowItems.forEach(item => newSelected.add(item.id))
                  onChange(Array.from(newSelected))
                }}
              >
                <i className="bx bx-check"></i>
                Select All Hidden
              </Button>

              <Button
                size="sm"
                color="outline-secondary"
                className="d-flex align-items-center gap-1"
                onClick={() => {
                  const newSelected = new Set(selectedSet)
                  overflowItems.forEach(item => newSelected.delete(item.id))
                  onChange(Array.from(newSelected))
                }}
              >
                <i className="bx bx-x"></i>
                Deselect All Hidden
              </Button>
            </div>
          </div>
        )}
      </div>
    ),
    [
      visibleItems,
      overflowItems,
      hasOverflow,
      expanded,
      selectedSet,
      toggleItem,
      onChange,
      toggleExpand,
    ]
  )

  return (
    <div className="button-checkbox-list">
      {isShowHeader ? headerSection : ""}

      {itemsContainer}

      {/* Selected items summary */}
      {showSelectionSummary && selectedSet.size > 0 && (
        <div className="mt-3 p-3 bg-success bg-opacity-10 rounded-3 border border-success border-opacity-25">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bx bx-check-circle text-success fs-5"></i>
            <h6 className="mb-0 text-success fw-medium">Selected Items</h6>
            <Badge color="success" className="ms-2">
              {selectedSet.size}
            </Badge>
          </div>
          <p className="text-muted mb-0 small">
            {items
              .filter(item => selectedSet.has(item.id))
              .map(item => item.name)
              .join(", ")}
          </p>
        </div>
      )}

      {/* Empty state */}
      {items.length === 0 && (
        <div className="text-center p-4 text-muted">
          <i className="bx bx-inbox fs-1 mb-2"></i>
          <p className="mb-0">No items available</p>
        </div>
      )}
    </div>
  )
}

export default ButtonCheckboxList
